
import { Component, Vue, Prop, Inject, Watch } from 'nuxt-property-decorator';
import {
  FormDto,
  FormFieldDto,
  IFourwavesApiService,
  FormEntryDto,
  FormFieldType,
  isFieldChoiceAvailable,
  FormFieldChoiceDto,
  FormSectionDto,
} from '../services';
import { Dictionary } from '../core';
import { InputOption } from '../types';
import InputText from '../components/design-system/input/input-text/InputText.vue';
import InputTextArea from '../components/design-system/input/input-text/InputTextArea.vue';
import InputRadioGroup from '../components/design-system/input/input-radio/InputRadioGroup.vue';
import InputCheckboxGroup from '../components/design-system/input/input-checkbox/InputCheckboxGroup.vue';
import InputSelect from '../components/design-system/input/input-select/InputSelect.vue';
import FormInputFile from './form-generator/FormInputFile.vue';
import FormFiguresInputFile from './form-generator/FormFiguresInputFile.vue';
import RichTextEditor from './form-generator/RichTextEditor.vue';
import Instructions from './form-generator/Instructions.vue';
import Authors from './form-generator/Authors.vue';
import Score from './form-generator/Score.vue';
import FormFieldHeader from './form/FormFieldHeader.vue';
import FormCountryList from './form/FormCountryList.vue';

@Component({
  components: {
    FormInputFile,
    FormFiguresInputFile,
    RichTextEditor,
    Instructions,
    Authors,
    Score,
    InputText,
    InputTextArea,
    InputCheckboxGroup,
    FormFieldHeader,
    InputRadioGroup,
    InputSelect,
    FormCountryList,
  },
})
export default class Form extends Vue {
  @Prop({ required: true }) readonly form!: FormDto;
  @Prop({ required: true }) formData!: Dictionary<string | string[]>;
  @Prop({ type: Boolean, default: false }) readonly isOrganizer!: boolean;
  @Prop({ required: false, default: null }) readonly formEntry!: FormEntryDto | null;
  @Prop({ type: String, default: 'CAD' }) readonly currency!: string;
  @Prop({ type: Boolean, default: false }) readonly tileLayout!: boolean;
  @Prop({ type: Boolean, default: false }) readonly disabled!: boolean;

  @Inject() $validator;
  @Inject() apiService!: IFourwavesApiService;

  readonly FormFieldType = FormFieldType;

  model: Dictionary<string | string[]> = {};
  hiddenFields: FormFieldDto[] = [];
  emailConfirmation = '';

  @Watch('model', { immediate: true, deep: true })
  public onModelChanged(val) {
    this.$emit('form-changed', val);
  }

  get paidChoiceIds(): string[] {
    return this.formEntry == null ? [] : this.formEntry.paidChoiceIds;
  }

  get filteredSections(): FormSectionDto[] {
    return this.form.formSections.filter(({ formFields }) =>
      formFields.some(field => !this.hiddenFields.find(x => x.id === field.id)),
    );
  }

  public async computeConditionalFields() {
    const ids = await this.apiService.getHiddenFieldsBasedOnFormData(this.form.id, this.model);
    const hiddenFields = this.form.formSections.flatMap(x => x.formFields).filter(x => ids.includes(x.id));

    hiddenFields.forEach(field => {
      // Clear form data associated to the field
      const resetValue = Array.isArray(this.model[field.id]) ? [] : '';
      this.$set(this.model, field.id, resetValue);
      this.$validator.errors.remove(field.id);
    });

    this.hiddenFields = hiddenFields;
  }

  public isEmailField(field: FormFieldDto): boolean {
    return field.id === this.form.emailFormFieldId;
  }

  async created() {
    if (Object.keys(this.formData).length > 0) {
      // Edit mode
      this.model = Object.assign({}, this.buildDataDictionary(this.form, this.formEntry, this.formData));
    } else if (this.form && this.form.formSections) {
      // Create mode
      for (const section of this.form.formSections) {
        for (const field of section.formFields) {
          if (field.fieldType === FormFieldType.Images || field.fieldType === FormFieldType.Checkboxes) {
            this.$set(this.model, field.id, []);
          } else {
            this.$set(this.model, field.id, '');
          }
        }
      }
    }

    // Set the confirmation email when user hit the 'Back' button
    if (this.form.emailFormFieldId && this.model[this.form.emailFormFieldId]) {
      this.emailConfirmation = this.model[this.form.emailFormFieldId] as string;
    }

    // Compute conditionals when form is loaded
    await this.computeConditionalFields();
  }

  get inputOptionsByFieldId() {
    const options = {};

    for (const section of this.form.formSections) {
      for (const field of section.formFields) {
        options[field.id] = this.computeInputOptions(field.formFieldChoices);
      }
    }

    return options;
  }

  public computeInputOptions(options: FormFieldChoiceDto[]): InputOption[] {
    return options.map(option => {
      const isChoiceAvailable = isFieldChoiceAvailable(option);
      return {
        id: option.id,
        label: this.transl8(option.label),
        disabled: this.isChoiceLocked(option) || !isChoiceAvailable,
        unavailable: !isChoiceAvailable,
      };
    });
  }

  public isChoiceLocked(option: FormFieldChoiceDto): boolean {
    if (!this.formEntry) return false;
    return this.formEntry!.formEntryValues.some(v => v.value === option.id) && this.paidChoiceIds.includes(option.id);
  }

  public paidSingleOptionLocked(field: FormFieldDto): boolean {
    return field.formFieldChoices.some(option => this.isChoiceLocked(option));
  }

  public isFileUploadField(fieldType: FormFieldType): boolean {
    return (
      fieldType === FormFieldType.File ||
      fieldType === FormFieldType.Poster ||
      fieldType === FormFieldType.Slides ||
      fieldType === FormFieldType.Images ||
      fieldType === FormFieldType.Video
    );
  }

  public getTrackValidation(field: FormFieldDto): string | null {
    // Enforce single selection when isMultiple is false and multiple tracks are present (edit mode)
    if (!field.isMultiple && Array.isArray(this.model[field.id])) {
      return 'single_track_selection';
    }
    return null;
  }
}
